/**
 * AutomationsSection — labeled section that renders a list of automation cards.
 * Reused on both the AI Automations page and the per-project Automations panel.
 *
 * Props:
 *   title         — section heading
 *   description   — short helper text below the heading
 *   icon          — phosphor icon component
 *   accent        — tailwind text-color class for the icon (e.g. "text-violet-500")
 *   automations   — list to render
 *   onOpen        — (automation) => void
 *   onToggle      — (id) => void
 *   onDelete      — (id) => void
 *   toggling      — id currently toggling
 *   deleting      — id currently deleting
 *   emptyHint     — string shown when the list is empty
 */
import AutomationCard from "./AutomationCard";

export default function AutomationsSection({
  title,
  description,
  icon: Icon,
  accent = "text-slate-400",
  automations,
  onOpen,
  onToggle,
  onDelete,
  toggling,
  deleting,
  emptyHint = "Nothing here yet.",
  testId,
}) {
  const count = automations.length;
  return (
    <section className="mb-8" data-testid={testId}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon size={16} className={accent} weight="fill" />}
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            {title}
            <span className="ml-2 text-[10px] font-mono font-medium text-slate-400">
              {count}
            </span>
          </h2>
        </div>
        {description && (
          <p className="text-[11px] text-slate-400 hidden md:block">{description}</p>
        )}
      </header>
      {count === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 py-6 text-center" data-testid={`${testId}-empty`}>
          <p className="text-xs text-slate-400">{emptyHint}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {automations.map((a) => (
            <AutomationCard
              key={a.id}
              automation={a}
              onToggle={onToggle}
              onDelete={onDelete}
              onOpen={onOpen}
              toggling={toggling}
              deleting={deleting}
            />
          ))}
        </div>
      )}
    </section>
  );
}
